// ═══════════════════════════════════════════════════════════════
// ProfileDashboard — dashboard data-driven
// Affiché à la place du dashboard legacy pour les users qui ont un
// profile_template_code. La config (palette, modules, cards, pills) est
// lue depuis /api/profile-templates/:code et appliquée dynamiquement.
// Ajouter un template = INSERT SQL, pas de rebuild React.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo } from 'react';
import {
  Shield, FlaskConical, Target, MessagesSquare, Brain,
  Share2, Layers, Globe, Lock, PlayCircle, ArrowRight,
  CheckCircle2, Bookmark, Coins, BarChart3, Users, Leaf,
  Compass, GraduationCap, Calculator, Scale, Sparkles,
  BookOpen, Feather, Map as MapIcon, MessageCircle, Box as BoxIcon, Gauge,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { toast } from '../ui/Toast';
import './ProfileDashboard.css';

/* ─── Palettes hardcodées par template (light/dark overlays) ───
   Le CSS attend des clés canoniques (azur, or, terracotta, pierre…). La palette
   DB peut diverger côté nommage (ex: DC utilise neon_cyan). Cet objet route
   chaque (template, mode) vers une palette React qui matche le CSS, ce qui
   garantit le respect du toggle clair/sombre même quand la palette DB est
   dark-first. Tomber sur `null` = utiliser `template.palette` (DB) tel quel.
   V2: colonnes palette_light/palette_dark JSONB dans profile_templates. */

// Template A — Commercial grand compte (palette sépia gold)
const DARK_PALETTE_A = {
  ink: '#E8E3D6', ink_soft: '#BFBBB0',
  parchemin: '#0A0D1A', elev: '#151A2E', deep: '#0E1220',
  azur: '#3B82F6', azur_soft: '#60A5FA',
  or: '#D4A340', or_clair: '#E8C47A',
  terracotta: '#C4623D', terra_soft: '#E07A52',
  pierre: '#8B8EA0', pierre_c: '#6A6E85',
  hairline: 'rgba(232, 227, 214, 0.12)', hairline_s: 'rgba(232, 227, 214, 0.06)',
};

// Template DC — Dev + Crypto (signature néon cyan/violet, dark-first)
// LIGHT : fond slate clair + accents cyan/violet sombres (contraste WCAG)
// DARK  : fond #0A0A0E + accents néon (cyan #00E5FF + violet #9D4EDD)
const LIGHT_PALETTE_DC = {
  ink: '#0F172A', ink_soft: '#334155',
  parchemin: '#F8FAFC', elev: '#F1F5F9', deep: '#E2E8F0',
  azur: '#0E7490', azur_soft: '#0891B2',
  or: '#A88A52', or_clair: '#92710A',
  terracotta: '#7C3AED', terra_soft: '#9D4EDD',
  pierre: '#475569', pierre_c: '#64748B',
  hairline: 'rgba(15, 23, 42, 0.12)', hairline_s: 'rgba(15, 23, 42, 0.06)',
};
const DARK_PALETTE_DC = {
  ink: '#E8EBF2', ink_soft: '#A8AEC0',
  parchemin: '#0A0A0E', elev: '#16161D', deep: '#1F1F28',
  azur: '#00E5FF', azur_soft: '#5BF5FF',
  or: '#D4B26A', or_clair: '#E8C47A',
  terracotta: '#9D4EDD', terra_soft: '#B070E8',
  pierre: '#8B8EA0', pierre_c: '#6A6E85',
  hairline: 'rgba(0, 229, 255, 0.18)', hairline_s: 'rgba(0, 229, 255, 0.08)',
};

// Template 2C — 2e cercle (palette neutre-premium, light-first en DB).
// DARK : fond neutre profond (pas sépia ni navy) + texte off-white + accents
// or/azur/terracotta de 2C éclaircis pour la lisibilité en mode sombre.
const DARK_PALETTE_2C = {
  ink: '#ECECEF', ink_soft: '#B4B4C0',
  parchemin: '#0D0D12', elev: '#16161D', deep: '#1F1F28',
  azur: '#3B82F6', azur_soft: '#60A5FA',
  or: '#D4B26A', or_clair: '#E8CC8E',
  terracotta: '#C4623D', terra_soft: '#E07A52',
  pierre: '#8A8A98', pierre_c: '#5A5A68',
  hairline: 'rgba(236, 236, 239, 0.12)', hairline_s: 'rgba(236, 236, 239, 0.06)',
};

// Mapping (templateCode → { light, dark }). null = fallback sur template.palette (DB).
const PALETTE_OVERRIDES = {
  A:   { light: null,              dark: DARK_PALETTE_A  },
  FD:  { light: null,              dark: DARK_PALETTE_A  },
  DC:  { light: LIGHT_PALETTE_DC,  dark: DARK_PALETTE_DC },
  '2C': { light: null,             dark: DARK_PALETTE_2C },
};

/* ─── Icon mapping (icon_name string → composant Lucide) ───── */
const ICON_MAP = {
  shield: Shield,
  flask: FlaskConical,
  target: Target,
  chat: MessagesSquare,
  brain: Brain,
  share: Share2,
  layers: Layers,
  globe: Globe,
  lock: Lock,
  check: CheckCircle2,
  bookmark: Bookmark,
  coins: Coins,
  chart: BarChart3,
  users: Users,
  leaf: Leaf,
  compass: Compass,
  graduation: GraduationCap,
  calculator: Calculator,
  scale: Scale,
  sparkles: Sparkles,
  'book-open': BookOpen,
  feather: Feather,
  map: MapIcon,
  'message-circle': MessageCircle,
  box: BoxIcon,
  gauge: Gauge,
};

function IconByName({ name, size = 22, strokeWidth = 1.25 }) {
  const Cmp = ICON_MAP[name] || Sparkles;
  return <Cmp size={size} strokeWidth={strokeWidth} />;
}

/* ─── Google Fonts loader (unique par session) ─────────────── */
/* Détecte les fonts à charger en scannant toutes les valeurs de typography,
   quel que soit le nom de la clé (body_font, display_font, mono_font,
   mono_function, accent_serif…). Permet à un template de déclarer des fonts
   sur des clés non standard sans casser le chargement. */
const loadedFonts = new Set();
const GOOGLE_FONT_QUERIES = {
  'Fraunces':       'Fraunces:ital,opsz,wght,SOFT,WONK@0,9..144,300..900,0..100,0..1;1,9..144,300..900,0..100,0..1',
  'DM Sans':        'DM+Sans:opsz,wght@9..40,300..700',
  'JetBrains Mono': 'JetBrains+Mono:wght@400;500;600',
  'Space Grotesk':  'Space+Grotesk:wght@400;500;600;700',
};
function loadGoogleFonts(fonts) {
  if (!fonts) return;
  const values = new Set(Object.values(fonts).filter(v => typeof v === 'string'));
  const families = [];
  for (const name of values) {
    if (GOOGLE_FONT_QUERIES[name]) families.push(GOOGLE_FONT_QUERIES[name]);
  }
  const key = families.join('|');
  if (!key || loadedFonts.has(key)) return;
  loadedFonts.add(key);

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${families.map(f => `family=${f}`).join('&')}&display=swap`;
  document.head.appendChild(link);
}

export default function ProfileDashboard({ templateCode, user, onProjectSelect }) {
  const [template, setTemplate] = useState(null);
  const [bases, setBases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [errored, setErrored] = useState(false);
  const { isDark } = useTheme();

  /* ─── Fetch template + user data ─── */
  useEffect(() => {
    if (!templateCode) return;
    const token = localStorage.getItem('oracle_token');
    if (!token) { setErrored(true); return; }

    Promise.all([
      fetch(`/api/profile-templates/${templateCode}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : Promise.reject(r.status)),
      fetch('/api/expertise-bases', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : []).catch(() => []),
      fetch('/api/projects', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : []).catch(() => []),
    ])
      .then(([tpl, basesData, projectsData]) => {
        setTemplate(tpl);
        setBases(Array.isArray(basesData) ? basesData : []);
        setProjects(Array.isArray(projectsData) ? projectsData : []);
        loadGoogleFonts(tpl?.typography);
      })
      .catch(() => setErrored(true));
  }, [templateCode]);

  /* ─── Palette inline style via CSS variables --pd-* ─── */
  /* Priorité : PALETTE_OVERRIDES[code][mode] > template.palette (DB). Permet aux
     templates "dark-first" (ex: DC) d'avoir aussi une palette light propre, et
     garantit que les clés exposées matchent celles attendues par le CSS
     (azur, or, terracotta, pierre…). La palette DB reste un catalogue
     documentaire et un fallback pour les templates sans overlay. */
  const paletteStyle = useMemo(() => {
    if (!template?.palette) return {};
    const overrides = PALETTE_OVERRIDES[template.code];
    const overlay = overrides ? (isDark ? overrides.dark : overrides.light) : null;
    const source = overlay || template.palette;
    const toCss = (key) => `--pd-${key.replace(/_/g, '-')}`;
    const style = {};
    for (const [k, v] of Object.entries(source)) style[toCss(k)] = v;
    if (template.typography) {
      const t = template.typography;
      if (t.display_font) style['--pd-font-display'] = `'${t.display_font}', serif`;
      if (t.body_font)    style['--pd-font-body']    = `'${t.body_font}', sans-serif`;
      // Accepter mono_font (standard) OU mono_function (variante DC)
      const monoFont = t.mono_font || t.mono_function;
      if (monoFont) style['--pd-font-mono'] = `'${monoFont}', monospace`;
    }
    return style;
  }, [template, isDark]);

  /* ─── Module states (intersection config + bases accessibles) ─── */
  const moduleStates = useMemo(() => {
    if (!template?.modules) return [];
    const map = new Map(bases.map(b => [b.slug, b]));
    return template.modules.map(m => {
      const base = map.get(m.slug);
      // Un module est "dispo" si :
      //  - une base d'expertise lui est rattachée avec au moins un document, OU
      //  - il porte lui-même un action_type qui le rend cliquable directement :
      //      · open_presentation → action_payload (URL HTML ouverte en nouvel onglet)
      //      · open_chat_with_preview → preview_url + prompt (chat projet + PreviewPane)
      //      · open_project (+ prompt optionnel)
      const hasAction = Boolean(
        (m.action_type === 'open_presentation' && m.action_payload) ||
        (m.action_type === 'open_chat_with_preview' && (m.preview_url || m.prompt)) ||
        (m.action_type === 'open_project')
      );
      return {
        ...m,
        hasContent: Boolean(base && (base.document_count > 0)) || hasAction,
        documentCount: base?.document_count || 0,
        baseId: base?.id || null,
        hasAction,
      };
    });
  }, [template, bases]);

  const projetPrincipal = useMemo(() => {
    if (projects.length === 0) return null;
    return projects.find(p => p.creator_id === user?.id) || projects[0];
  }, [projects, user]);

  const timeGreeting = useMemo(() => {
    if (template?.hero_greeting_mode && template.hero_greeting_mode !== 'time_based') {
      return template.hero_greeting_mode; // fixed greeting
    }
    const h = new Date().getHours();
    if (h < 12) return 'Bonjour';
    if (h < 18) return 'Bon après-midi';
    return 'Bonsoir';
  }, [template]);

  const prenom = user?.full_name?.split(' ')[0] || user?.username || '';

  // Ouvre le projet principal. Si `item` porte un `prompt`, il est déposé en
  // sessionStorage → ProjectDetail crée une nouvelle session et pré-remplit le chat.
  // Pattern partagé par cards, modules et pills : un clic = un thread dédié.
  //
  // action_type='open_chat_with_preview' (modules II-V template FD) :
  //   ajoute profile_initial_preview_url + profile_initial_prompt_auto_send
  //   → ProjectDetail ouvre le PreviewPane avec le HTML + déclenche l'envoi automatique
  const openWithPrompt = (item) => {
    // Legacy : action_type=open_presentation → nouvel onglet
    if (item?.action_type === 'open_presentation' && item?.action_payload) {
      window.open(item.action_payload, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!projetPrincipal) {
      toast.warning('Espace projet non configuré, contacte Ben pour initialiser ton dashboard');
      return;
    }
    if (!onProjectSelect) return;
    // Mode cinéma (card "Lire la présentation") : ouvre le projet avec preview maximisé
    // + repli des deux sidebars (gauche navigation + droite FilesSidebar). Pas de prompt
    // donc pas de chat auto, juste lecture. ProjectDetail consomme ces flags.
    if (item?.cinema_mode) sessionStorage.setItem('profile_initial_cinema_mode', 'true');
    else sessionStorage.removeItem('profile_initial_cinema_mode');
    if (item?.pdf_url) sessionStorage.setItem('profile_initial_pdf_url', item.pdf_url);
    else sessionStorage.removeItem('profile_initial_pdf_url');
    const prenom = user?.full_name?.split(' ')[0] || user?.username || '';
    try {
      if (item?.prompt) {
        // Split prompt : partie visible (question) + partie cachée (règles anti-hallucination / consigne)
        // Les règles commencent par un bloc [RÈGLES...], [PRÉCISION...], ou [CONSIGNE...] — séparé par \n\n[
        const sepRegex = /\n\n\[(RÈGLES|PRÉCISION|CONSIGNE)/;
        // Remplacement {{prenom}} avant split — concerne surtout le bloc [CONSIGNE] pour l'accueil personnalisé
        const filledPrompt = item.prompt.replace(/\{\{prenom\}\}/g, prenom);
        const match = filledPrompt.match(sepRegex);
        let question = filledPrompt;
        let addendum = '';
        if (match && match.index > 0) {
          question = filledPrompt.substring(0, match.index).trim();
          addendum = filledPrompt.substring(match.index).trim();
        }
        sessionStorage.setItem('profile_initial_prompt', question);
        if (addendum) sessionStorage.setItem('profile_initial_system_addendum', addendum);
        else sessionStorage.removeItem('profile_initial_system_addendum');
        sessionStorage.setItem('profile_initial_prompt_key', item.key || item.slug || item.roman || '');
      }
      // Forçage modèle optionnel (ex: cards commerciales → Sonnet pour éviter hallucinations Haiku)
      if (item?.model) sessionStorage.setItem('profile_initial_model', item.model);
      else sessionStorage.removeItem('profile_initial_model');
      // Auto-send : modules II-V déclenchent l'envoi dès que la session est créée
      if (item?.auto_send) sessionStorage.setItem('profile_initial_prompt_auto_send', 'true');
      else sessionStorage.removeItem('profile_initial_prompt_auto_send');
      // Preview URL : ouvre le PreviewPane droite avec le HTML de présentation
      if (item?.preview_url) sessionStorage.setItem('profile_initial_preview_url', item.preview_url);
      else sessionStorage.removeItem('profile_initial_preview_url');
    } catch (e) { /* silent */ }
    onProjectSelect(projetPrincipal);
  };
  const openChatWithPrompt = openWithPrompt;
  const openProject = () => openWithPrompt(null);

  /* ─── États de rendu ─── */
  if (errored) {
    return (
      <div className="profile-dash">
        <div className="pd-loading">Impossible de charger le template — réessayez plus tard.</div>
      </div>
    );
  }
  if (!template) {
    return (
      <div className="profile-dash">
        <div className="pd-loading">Chargement de votre espace…</div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const heure = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const glyphe = template.glyph_sep || '§';

  return (
    <div className="profile-dash" style={paletteStyle}>

      {/* ─── HERO ─── */}
      <div className="pd-hero-section">
        <div className="pd-eyebrow">{today} · {heure}</div>
        <h1 className="pd-hero">
          {timeGreeting}
          {prenom ? <>, <em>{prenom}.</em></> : <>.</>}
        </h1>
        {template.hero_subtitle && (
          <p className="pd-sub">{template.hero_subtitle}</p>
        )}
      </div>

      {/* ─── ACTION CARDS BENTO ─── */}
      {template.cards?.length > 0 && (
        <div className="pd-bento">
          {template.cards.map((card, idx) => (
            <button
              key={idx}
              className={`pd-card ${card.variant || 'default'}`}
              onClick={() => openWithPrompt(card)}
            >
              <div>
                {card.roman && <div className="roman">{card.roman}</div>}
                <div className="icon"><IconByName name={card.icon} /></div>
                <div className="title">{card.title}</div>
                <div className="subtitle">{card.subtitle}</div>
              </div>
              {card.meta && <div className="meta">{card.meta}</div>}
            </button>
          ))}
        </div>
      )}

      {/* ─── SEP ─── */}
      <div className="pd-glyph-sep"><span>{glyphe} &nbsp; Parcours pédagogique &nbsp; {glyphe}</span></div>

      {/* ─── PARCOURS BLOC ─── */}
      <div className="pd-parcours">
        <div className="pd-parcours-header">
          <div>
            <div className="pd-parcours-title">{template.modules?.length || 0} modules, trois niveaux.</div>
            <div className="pd-parcours-sub">Métaphore, vocabulaire, technique — lis chaque module selon le temps dont tu disposes.</div>
          </div>
          {projetPrincipal && (
            <button className="pd-btn" onClick={openProject}>
              <PlayCircle size={14} strokeWidth={1.5} />
              Ouvrir mon projet
            </button>
          )}
        </div>

        {moduleStates.map((m) => (
          <button
            key={m.slug}
            className={`pd-line ${m.hasContent ? 'has-content' : ''}`}
            onClick={() => openWithPrompt(m)}
          >
            <span className="roman-num">{m.roman}</span>
            <span className="dot"></span>
            <div style={{ marginRight: 'auto', flex: 1 }}>
              <div className="line-title">{m.title}</div>
              <div className="line-sub">{m.sub}</div>
            </div>
            <span className="line-badge">
              {m.hasAction
                ? 'Disponible'
                : m.hasContent
                  ? `${m.documentCount} doc${m.documentCount > 1 ? 's' : ''}`
                  : 'À venir'}
            </span>
          </button>
        ))}
      </div>

      {/* ─── SEP ─── */}
      <div className="pd-glyph-sep"><span>✧ &nbsp; Conversation guidée &nbsp; ✧</span></div>

      {/* ─── PILLS ─── */}
      {template.pills?.length > 0 && (
        <div className="pd-pills-row">
          {template.pills.map((pill) => (
            <button
              key={pill.key}
              className="pd-pill"
              onClick={() => openChatWithPrompt(pill)}
            >
              <IconByName name={pill.icon} size={14} strokeWidth={1.5} />
              {pill.label}
            </button>
          ))}
        </div>
      )}

      {/* ─── CTA ─── */}
      {template.cta_text && (
        <div className="pd-cta">
          <div className="pd-cta-text">{template.cta_text}</div>
          {projetPrincipal && (
            <button className="pd-btn" onClick={openProject}>
              Ouvrir
              <ArrowRight size={14} strokeWidth={2} />
            </button>
          )}
        </div>
      )}

      <div style={{ height: 32 }} />
    </div>
  );
}
